'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { X } from 'lucide-react'

import type {
  CosmeticProfileEntry,
  CosmeticProfileKind
} from '@/features/profiles/types/avatar-catalog'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/Button'

import { fireCosmeticRewardConfetti } from './cosmetic-reward-confetti'

function redeemItemImageSrc(entry: CosmeticProfileEntry): string {
  return entry.image?.file?.url ?? '/placeholders/avatar.png'
}

function CosmeticRedeemItemPreview({ entry }: { entry: CosmeticProfileEntry }) {
  const src = redeemItemImageSrc(entry)
  const kind = entry.kind as CosmeticProfileKind
  const ringShadow =
    'ring-2 ring-amber-400/35 shadow-[0_0_32px_-8px_rgba(250,204,21,0.45)]'

  if (kind === 'avatar') {
    return (
      <div
        className={cn(
          'mx-auto aspect-square w-full max-w-[220px] overflow-hidden rounded-2xl',
          ringShadow
        )}
      >
        <img
          src={src}
          alt=""
          className="size-full object-cover"
          width={220}
          height={220}
        />
      </div>
    )
  }

  if (kind === 'background') {
    return (
      <div
        className={cn(
          'mx-auto aspect-square w-full max-w-[260px] overflow-hidden rounded-2xl',
          ringShadow
        )}
      >
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          width={400}
          height={400}
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        'mx-auto flex aspect-square w-full max-w-[260px] items-center justify-center overflow-hidden rounded-2xl bg-black/25',
        ringShadow
      )}
    >
      <img
        src={src}
        alt=""
        className="max-h-full max-w-full object-contain"
        width={512}
        height={512}
      />
    </div>
  )
}

type Phase = 'idle' | 'pending' | 'success'

export interface CosmeticRedeemModalProps {
  entry: CosmeticProfileEntry
  onClose: () => void
  onUnlock: (
    entry: CosmeticProfileEntry,
    options?: { silentGlobalSuccess?: boolean }
  ) => Promise<{ ok: boolean; errorMessage?: string }>
}

export function CosmeticRedeemModal({
  entry,
  onClose,
  onUnlock
}: CosmeticRedeemModalProps) {
  const titleId = useId()
  const descId = useId()
  const closeRef = useRef<HTMLButtonElement>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => closeRef.current?.focus(), 50)
    return () => window.clearTimeout(t)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (phase === 'pending') return
    setLocalError(null)
    setPhase('pending')
    const result = await onUnlock(entry, { silentGlobalSuccess: true })
    if (result.ok) {
      setPhase('success')
      fireCosmeticRewardConfetti()
      window.setTimeout(() => {
        onClose()
      }, 2400)
    } else {
      setPhase('idle')
      setLocalError(
        result.errorMessage ?? 'Não foi possível resgatar. Tente novamente.'
      )
    }
  }, [entry, onClose, onUnlock, phase])

  const lines: string[] = []
  const c = entry.criteria
  if (c.points_cost != null && c.points_cost > 0) {
    lines.push(`${c.points_cost} pts`)
  }
  if (c.mission_ids?.length) {
    lines.push(`${c.mission_ids.length} missão(ões)`)
  }

  return (
    <div className="w-full flex justify-center">
      <div
        className={cn(
          'relative w-full max-w-[min(100%,380px)] overflow-hidden rounded-2xl',
          'border-2 border-[#f4d03f]/90',
          'bg-[linear-gradient(180deg,#2d1b3d_0%,#1f1228_48%,#160d1f_100%)]',
          'shadow-[0_0_0_1px_rgba(255,215,80,0.35),0_12px_40px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.12)]'
        )}
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg,transparent,transparent 2px,rgba(255,255,255,0.03) 2px,rgba(255,255,255,0.03) 4px)'
          }}
        />
        <button
          ref={closeRef}
          type="button"
          disabled={phase === 'pending'}
          className={cn(
            'absolute top-2.5 right-2.5 z-20 flex size-9 items-center justify-center rounded-full',
            'bg-black/35 text-amber-100/90 ring-1 ring-amber-400/40 hover:bg-black/50',
            'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/80',
            phase === 'pending' && 'opacity-40 pointer-events-none'
          )}
          onClick={onClose}
        >
          <X className="size-4" strokeWidth={2.5} />
        </button>

        <div className="relative px-4 pb-4 pt-5 sm:px-5 sm:pb-5 sm:pt-6">
          <div className="mb-1 text-center">
            <span
              className="inline-block rounded-full border border-amber-400/50 bg-amber-400/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-200/95"
              aria-hidden
            >
              Recompensa
            </span>
          </div>
          <h2
            id={titleId}
            className="text-center font-sans text-lg font-extrabold uppercase tracking-wide text-amber-100 drop-shadow-[0_1px_0_rgba(0,0,0,0.5)] sm:text-xl"
          >
            {phase === 'success' ? 'Resgatado!' : 'Resgatar item'}
          </h2>
          <p
            id={descId}
            className="mt-1.5 text-center text-sm font-medium text-violet-100/75"
          >
            {phase === 'success' ? (
              `${entry.name} foi adicionado à sua coleção.`
            ) : (
              <>
                Confirme para adicionar <b>{entry.name}</b> à sua coleção.
              </>
            )}
          </p>

          <div
            className="mx-auto mt-4 max-w-[260px]"
            aria-label={`Item: ${entry.name}`}
          >
            <CosmeticRedeemItemPreview entry={entry} />
          </div>

          {lines.length > 0 && phase !== 'success' && (
            <p className="mt-3 text-center text-[11px] text-violet-200/55">
              {lines.join(' · ')}
            </p>
          )}

          {localError && (
            <p
              className="mt-3 text-center text-xs font-medium text-red-300/95"
              role="alert"
            >
              {localError}
            </p>
          )}

          {phase !== 'success' && (
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={phase === 'pending'}
                className="flex-1"
                onClick={onClose}
              >
                Voltar
              </Button>
              <Button
                type="button"
                variant="reward"
                loading={phase === 'pending'}
                className="flex-1"
                onClick={() => void handleConfirm()}
              >
                {phase === 'pending' ? 'Resgatando…' : 'Confirmar resgate'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
